import { Button, Checkbox, Dialog, Div, Drawer, FormRow, H2, P, Section, Space, Table, Td, Th, Tr } from '@dnb/eufemia';
import { trash as TrashIcon } from '@dnb/eufemia/icons';
import { hourglass as HourglassIcon, stop as StopIcon } from '@dnb/eufemia/icons';
import styled from '@emotion/styled';
import { Decision, type EntitlementRequestWithScopeDto } from '@portals/shared/portal/AppApiEntitlementDto';
import useAsync from '@portals/shared-frontend/hooks/useAsync';
import { type JSX, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { mutate } from 'swr';

import { acknowledgeDenial, cancelPendingRequest } from '@/api/app';
import Card from '@/components/Card';
import LoadingModal from '@/components/LoadingModal';

import ScopeCard from '../ScopeCard';
import RequestTimeline from './components/RequestTimeline';

import style from './index.module.css';

type EntitlementRequestProps = {
  entitlementRequests: EntitlementRequestWithScopeDto[];
  isAdmin: boolean;
};

function convertDecisionToText(status: Decision) {
  const Status = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
  `;
  switch (status) {
    case Decision.PRESUBMIT: {
      return (
        <Status>
          <HourglassIcon />
          <P left="x-small">Processing</P>
        </Status>
      );
    }
    case Decision.PENDING: {
      return (
        <Status>
          <HourglassIcon />
          <P left="x-small">Waiting for review</P>
        </Status>
      );
    }
    case Decision.DENIED: {
      return (
        <Status>
          <StopIcon />
          <P left="x-small">Your access is denied</P>
        </Status>
      );
    }
    case Decision.LEVEL1_APPROVED: {
      return (
        <Status>
          <StopIcon />
          <P left="x-small">Waiting for final approval</P>
        </Status>
      );
    }
    default: {
      return 'Unknown status';
    }
  }
}

export default function EntitlementRequest({ entitlementRequests, isAdmin }: EntitlementRequestProps): JSX.Element {
  const { id: appId } = useParams();

  return (
    <Card>
      <Table className={style['Table']}>
        <thead>
          <Tr>
            <Th>API</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </thead>
        <tbody>
          {entitlementRequests.map((request) => (
            <Tr key={request.id}>
              <Td>{request.apiName}</Td>
              <Td>{convertDecisionToText(request.decision)}</Td>
              <Td>
                <Drawer key={request.id} title="Your request" triggerAttributes={{ text: 'View' }}>
                  <Drawer.Navigation />
                  <Drawer.Header>
                    <P bottom>The list below displays the scopes you have requested access for.</P>
                  </Drawer.Header>
                  <Drawer.Body>
                    {appId && <EntitlementRequestItem appId={appId} isAdmin={isAdmin} request={request} />}
                  </Drawer.Body>
                </Drawer>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function EntitlementRequestItem({
  request,
  isAdmin,
  appId,
}: {
  request: EntitlementRequestWithScopeDto;
  isAdmin: boolean;
  appId: string;
}): JSX.Element {
  const [userAck, setUserAck] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const onCancelRequest = useAsync(
    async (apiId: string, entitlementRequestId: string) => {
      if (appId) {
        await cancelPendingRequest(appId, apiId, entitlementRequestId);
        mutate(`/apps/${appId}`);
        setShowCancelDialog(false);
      }
    },
    [appId],
  );

  const onAcknowledgeDenial = useAsync(
    async (apiId: string, entitlementRequestId: string) => {
      if (appId) {
        await acknowledgeDenial(appId, apiId, entitlementRequestId);
        mutate(`/apps/${appId}`);
        setShowCancelDialog(false);
      }
    },
    [appId],
  );

  const grouppedRequest = useMemo(() => {
    return [
      ...request.scopes.map((s) => ({ ...s, env: 'Live' })),
      ...request.testScopes.map((s) => ({
        ...s,
        env: 'Test',
      })),
    ].reduce(
      (
        acc: Array<{
          name: string;
          description: string;
          id: string;
          envs: string[];
        }>,
        scope,
      ) => {
        const existing = acc.find((s) => s.name === scope.name);
        if (existing) {
          if (!existing.envs.includes(scope.env)) {
            existing.envs.push(scope.env);
          }
        } else {
          acc.push({
            name: scope.name,
            description: scope.description,
            id: scope.id,
            envs: [scope.env],
          });
        }
        return acc;
      },
      [],
    );
  }, [request.scopes, request.testScopes]);
  return (
    <Section spacing="x-large" style_type="transparent">
      {(onCancelRequest.waiting || onAcknowledgeDenial.waiting) && <LoadingModal />}
      <H2>{request.apiName}</H2>
      <RequestTimeline request={request} />
      {request.scopes.length === 0 && request.testScopes.length === 0 && (
        <ScopeCard
          environments={['Test', 'Live']}
          reviewStatus={request.decision}
          scopeDescription="Request for full access"
          scopeId="all"
          scopeName="Full access"
          showAccessSection
        />
      )}
      {grouppedRequest.map((scope) => (
        <Space bottom="small" key={scope.name}>
          <ScopeCard
            environments={scope.envs}
            reviewStatus={request.decision}
            scopeDescription={scope.description}
            scopeId={scope.id}
            scopeName={scope.name}
            showAccessSection
          />
        </Space>
      ))}
      {isAdmin &&
        (request.decision == Decision.DENIED ? (
          <>
            <Div top="medium">
              <Checkbox
                label="I have read and understood why my request was rejected. "
                onChange={({ checked }) => setUserAck(checked)}
              />
            </Div>

            <Button
              disabled={!userAck}
              on_click={() => onAcknowledgeDenial.execute(request.apiId, request.id)}
              text="Confirm and close"
              top="small"
            />
          </>
        ) : (
          <FormRow direction="horizontal" top="small">
            <Button
              icon={TrashIcon}
              icon_position="left"
              on_click={() => setShowCancelDialog(true)}
              text="Cancel Request"
              variant="signal"
            />
            <Dialog
              confirmText="Confirm"
              description={
                <>Canceling a request completely deletes it. You can make a new request whenever you like.</>
              }
              onConfirm={() => onCancelRequest.execute(request.apiId, request.id)}
              onDecline={() => setShowCancelDialog(false)}
              openState={showCancelDialog}
              title="Are you sure you want to cancel the request?"
              triggerAttributes={{
                hidden: true,
              }}
              variant="confirmation"
            />
          </FormRow>
        ))}
    </Section>
  );
}
